import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Users, Calendar, DollarSign, Camera } from 'lucide-react';

function getResultIcon(type) {
  switch (type) {
    case 'document':
      return <FileText className="w-4 h-4" />;
    case 'client':
      return <Users className="w-4 h-4" />;
    case 'meeting':
    case 'production':
      return <Calendar className="w-4 h-4" />;
    case 'transaction':
      return <DollarSign className="w-4 h-4" />;
    default:
      return <Camera className="w-4 h-4" />;
  }
}

function getResultLabel(type) {
  switch (type) {
    case 'document':
      return 'Document';
    case 'client':
      return 'Client';
    case 'meeting':
      return 'Meeting';
    case 'production':
      return 'Shoot';
    case 'transaction':
      return 'Transaction';
    default:
      return type;
  }
}

function SearchResultItem({ result, onSelect }) {
  const navigate = useNavigate();

  const handleClick = () => {
    if (result.type === 'client') {
      navigate(`/clients/${result.id}`);
    } else if (result.type === 'document') {
      navigate('/documents');
    } else if (result.type === 'meeting' || result.type === 'production') {
      navigate('/schedule');
    } else if (result.type === 'transaction') {
      navigate('/money');
    }
    onSelect();
  };

  return (
    <button
      onClick={handleClick}
      className="w-full text-left px-4 py-3 hover:bg-surface-raised transition-colors flex items-start gap-3 border-b border-border last:border-0"
    >
      <div className="mt-0.5 text-primary">{getResultIcon(result.type)}</div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className="font-medium text-foreground">{result.title}</span>
          <span className="text-xs px-2 py-0.5 bg-primary/10 text-primary rounded">
            {getResultLabel(result.type)}
          </span>
        </div>
        {result.subtitle && (
          <p className="text-sm text-text-secondary truncate">{result.subtitle}</p>
        )}
        {result.date && (
          <p className="text-xs text-text-muted mt-1">
            {new Date(result.date).toLocaleDateString()}
          </p>
        )}
      </div>
    </button>
  );
}

export function SearchResults({ results, onSelect, query }) {
  if (!query) {
    return (
      <div className="px-4 py-8 text-center text-text-muted">
        Type to search across clients, documents, meetings, and transactions
      </div>
    );
  }

  if (results.length === 0) {
    return (
      <div className="px-4 py-8 text-center">
        <p className="text-text-muted mb-2">No results found for "{query}"</p>
        <p className="text-sm text-text-secondary">
          Try searching for a client name, document title, or transaction description
        </p>
      </div>
    );
  }

  return (
    <div className="max-h-[400px] overflow-y-auto">
      {results.map((result, index) => (
        <SearchResultItem key={`${result.type}-${result.id}-${index}`} result={result} onSelect={onSelect} />
      ))}
    </div>
  );
}
